import { useEffect, useRef, useState } from 'react';
import { store as _store } from '@risingstack/react-easy-state';
import { useSelector } from 'react-redux';
import { modalSelectors } from 'framework/selectors';
import { addProgressBar } from 'framework/components/ui/ProgressBar/useProgressBar';
import { IconNames } from 'framework/components/ui/Icon';
import { useModal } from 'framework/components/ui/Modal';
import { fetchData, fileUpload } from 'framework/api/fetch-data';
import { SubmitResult } from 'framework/components/ui/FormComponents/Form/Form.interface';
import { AppError } from 'types';
import config from 'config';
import { toastError, toastSuccess, toastWarning } from 'stores/toast';
import { removeSpaceAndSpecialCharacter, renameFile } from 'framework/utils/helpers';
import { useLocation } from 'react-router-dom';
import ApplicationMonitoring from 'framework/applicationMonitoring/ApplicationMonitoring';
import { localImgId } from 'framework/components/ui/Img/Img';
import { Uuid } from 'framework/utils/generateId';
import { i18n } from 'i18n';
import {
  fetchFolders,
  formatChildren,
  attachChildren,
  deleteFileFolder,
  checkFilesSizePass,
  selectUploadEndpoint,
  handleMultiFileResponse,
  moveMultipleFiles,
  searchFiles,
  mapSearchResults,
  deleteMultipleFiles,
} from './services';
import {
  FileFolder,
  FileFolderType,
  MissingData,
  ExplorerMode,
  UploaderFileManagerProps,
  ServerFile,
  RootFileManagerProps,
  ExplorerFileManagerProps,
  MoveFilesResponse,
  MultiFileUpload,
  DeleteFilesResponse,
} from './types';
import { fileExtensionMapping, FILEMANAGER_MODAL, FILEMANAGER_MODAL_MOVE_TO_FOLDER } from './constants';
import { useFileManagerContext } from './FileManagerContext';
import { useFeatureIsOn } from '../../../../growthbook/useFeatureIsOn';

export const extractFileReferences = (files: FileFolder[]): string[] => files.map((file) => file.reference);

export const formatFailedPaths = (failedMoves: MoveFilesResponse['failedMoves']): string =>
  failedMoves.map((f) => f.sourceFilePath).join(', ');

export const isAllSuccess = (response: MoveFilesResponse | DeleteFilesResponse): boolean =>
  response.successCount > 0 && response.failureCount === 0;

export const isPartialSuccess = (response: MoveFilesResponse | DeleteFilesResponse): boolean =>
  response.successCount > 0 && response.failureCount > 0;

export const isAllFailure = (response: MoveFilesResponse | DeleteFilesResponse): boolean =>
  response.failureCount > 0 && response.successCount === 0;

export const isDirectoryRow = (row: FileFolder): boolean => row.type === FileFolderType.DIRECTORY;

export const shouldRefreshRoot = (activeFolder?: FileFolder): boolean => !activeFolder;

export const shouldNavigateToParent = (folder: FileFolder): boolean => !!folder.parent;

export const hasDestinationFolder = (destinationFolder?: FileFolder): boolean => !!destinationFolder;

export const hasMovableFiles = (movableFiles?: FileFolder[]): boolean => !!(movableFiles && movableFiles.length > 0);

export const hasSingleMovableFile = (movableFile?: FileFolder): boolean => !!movableFile;

export const isMultipleFilesError = (files: File[]): boolean => files.length > 1;

export const hasFiles = (files: File[]): boolean => files.length > 0;

export const getStore = () => {
  // These are destructured for future use/side effects from context.
  const {
    setActiveFolder,
    setPreviewableFile,
  } = useFileManagerContext();

  const store = _store({
    isOpen: false,
    initializing: true,
    loadingReference: undefined as string,
    data: [] as FileFolder[],
    dataCopy: [] as FileFolder[],
    searchTerm: undefined as string,

    initializeStore: addProgressBar(async (open: boolean) => {
      store.isOpen = open;
      if (store.isOpen) {
        store.searchTerm = undefined;
        store.initializing = true;
        await fetchFolders('', true)
          .then((res) => {
            const formattedChildren = formatChildren(res.data ?? []);
            store.data = formattedChildren;
            store.dataCopy = [...formattedChildren];
          })
          .finally(() => {
            store.initializing = false;
          });
      }
    }),

    isLoading: (ref: string) => store.loadingReference === ref,

    resolveIcon: (row: FileFolder): IconNames => {
      if (row.type === FileFolderType.DIRECTORY) {
        return 'folder';
      }
      return fileExtensionMapping[row.name.split('.').pop()];
    },

    fetchSubFolders: addProgressBar(async (row: FileFolder, includeFiles: boolean = true) => {
      if (store?.searchTerm?.length) {
        await store.searchFiles(store.searchTerm);
        return undefined;
      }
      store.loadingReference = row.type === FileFolderType.DIRECTORY ? row.reference : undefined;
      return fetchFolders(row.reference, includeFiles)
        .then((res) => {
          store.data = attachChildren(row.reference, store.data, formatChildren(res.data));
          return res.data;
        })
        .finally(() => {
          store.loadingReference = undefined;
        });
    }),

    searchFiles: addProgressBar(async (searchTerm: string) => {
      // Only perform a search if the search term is more than 3 characters
      if (searchTerm?.length <= 3) {
        // Reinitialize the file manager if search term is empty
        if (searchTerm?.length === 0) {
          store.data = store.dataCopy;
          store.searchTerm = undefined;
          setActiveFolder(undefined);
          setPreviewableFile(undefined);
        }
        return;
      }

      // Reset explorer
      setActiveFolder(undefined);
      setPreviewableFile(undefined);

      store.searchTerm = searchTerm;
      store.initializing = true;

      await searchFiles(searchTerm)
        .then((res) => {
          store.data = mapSearchResults(store.dataCopy, res.data ?? []);
        })
        .finally(() => {
          store.initializing = false;
        });
    }),

    rowClick: async (
      row: FileFolder,
      includeFiles: boolean = true,
    ) => store.fetchSubFolders(row, includeFiles).then((res) => res),
  });
  return store;
};

type BaseFileManagerProps = {
  modalName?: string;
};

export const useBaseFileManager = ({ modalName }: BaseFileManagerProps) => {
  const store = useRef(getStore()).current;
  const [missingData, setMissingData] = useState<MissingData>();
  const modalIsOpen = useSelector((state) => modalSelectors.isModalOpen(state, modalName));

  // fetch folders when modal is mounted
  useEffect(() => {
    store.initializeStore(!modalName || modalIsOpen);
  }, [modalIsOpen]);

  return {
    store,
    missingData,
    setMissingData,
  };
};

export const useExplorerFileManager = ({ explorerModalName = FILEMANAGER_MODAL }: ExplorerFileManagerProps) => {
  const base = useBaseFileManager({ modalName: explorerModalName });
  const { openModal } = useModal(FILEMANAGER_MODAL_MOVE_TO_FOLDER);

  const { pathname } = useLocation();

  const { setActiveFolder, setPreviewableFile, previewableFile, activeFolder } = useFileManagerContext();

  const explorerIsOpen = useSelector((state) => modalSelectors.isModalOpen(state, explorerModalName));

  const { store } = base;

  const openMoveModal = (file?: FileFolder) => {
    if (file) {
      setPreviewableFile(file);
    }
    openModal();
  };

  const handleExplorerRowClick = async (row: FileFolder) => {
    ApplicationMonitoring.trackEvent(
      row?.trackEventProps?.name || pathname,
      row?.trackEventProps?.value || 'Explorer row',
      row?.trackEventProps?.label || row?.name
    );
    if (row.type === FileFolderType.DIRECTORY) {
      // do not load folder if there is an active search term and children have already
      // been loaded
      const res = store.searchTerm && row.children?.length > 0 ? row.children : await store.rowClick(row);
      setPreviewableFile(undefined);
      setActiveFolder(row);
      return res;
    }
    setPreviewableFile(row);
    return store.data;
  };

  const refreshActiveFolder = () => {
    setPreviewableFile(undefined);
    if (shouldRefreshRoot(activeFolder)) {
      store.initializeStore(true);
    } else {
      store.fetchSubFolders(activeFolder, true);
    }
  };

  const handleFileDelete = (file: FileFolder) =>
    deleteFileFolder(file.reference).then(() => {
      refreshActiveFolder();
    });

  const handleActiveFolderDelete = () => {
    const { parent } = activeFolder;
    deleteFileFolder(activeFolder.reference).then(() => {
      if (shouldNavigateToParent(activeFolder)) {
        handleExplorerRowClick(parent);
      } else {
        store.initializeStore(true);
      }
    });
  };

  useEffect(() => {
    if (!explorerIsOpen) {
      setPreviewableFile(undefined);
      setActiveFolder(undefined);
    }
  }, [explorerIsOpen]);

  return {
    ...base,
    activeFolder,
    previewableFile,
    openMoveModal,
    handleFileDelete,
    setPreviewableFile,
    refreshActiveFolder,
    handleExplorerRowClick,
    handleActiveFolderDelete,
  };
};

export type MoveFolderFileManagerProps = {
  movableFile?: FileFolder;
  movableFiles?: FileFolder[];
  onFileMoved?: () => void;
};

export const handleMultipleFileMove = async (destinationPath: string, files: FileFolder[], onSuccess: () => void) => {
  const fileReferences = extractFileReferences(files);
  const res = await moveMultipleFiles(destinationPath, fileReferences, false);

  if (res.success) {
    if (isAllSuccess(res.data) || isPartialSuccess(res.data)) {
      toastSuccess(
        i18n.t('modules.contentmanagement.filesMovedSuccessfully', {
          count: res.data.successCount,
        })
      );
    }

    if (res.data.failureCount > 0) {
      const failedFiles: string = res.data.failedMoves.map((file) => file.sourceFilePath).toString();
      const toastFn = isAllFailure(res.data) ? toastError : toastWarning;
      toastFn(
        i18n.t('modules.contentmanagement.filesMoveFailed', {
          count: res.data.failureCount,
          files: failedFiles,
        }),
        50000
      );
    }

    onSuccess();
  } else {
    toastError(i18n.t('modules.contentmanagement.filesMoveError'));
  }
};

export const handleMultipleFileDelete = async (files: FileFolder[], onSuccess: () => void) => {
  const fileReferences = extractFileReferences(files);
  const res = await deleteMultipleFiles(fileReferences);

  if (res.success) {
    if (isAllSuccess(res.data) || isPartialSuccess(res.data)) {
      toastSuccess(
        i18n.t('modules.contentmanagement.filesDeletedSuccessfully', {
          count: res.data.successCount,
        })
      );
    }

    if (res.data.failureCount > 0) {
      const failedFiles: string = res.data.failedDeletes.map((file) => file.filePath).toString();
      const toastFn = isAllFailure(res.data) ? toastError : toastWarning;
      toastFn(
        i18n.t('modules.contentmanagement.filesDeletedFailed', {
          count: res.data.failureCount,
          files: failedFiles,
        }),
        50000
      );
    }

    onSuccess();
  } else {
    toastError(i18n.t('modules.contentmanagement.filesDeletedError'));
  }
};

export const useMoveFolderFileManager = ({ movableFile, movableFiles, onFileMoved }: MoveFolderFileManagerProps) => {
  const base = useBaseFileManager({ modalName: FILEMANAGER_MODAL_MOVE_TO_FOLDER });
  const [destinationFolder, setDestinationFolder] = useState<FileFolder>();
  const { closeModal } = useModal(FILEMANAGER_MODAL_MOVE_TO_FOLDER);
  const isModalOpen = useSelector((state) => modalSelectors.isModalOpen(state, FILEMANAGER_MODAL_MOVE_TO_FOLDER));

  const { store, setMissingData } = base;

  useEffect(() => {
    if (!isModalOpen) {
      setDestinationFolder(undefined);
    }
  }, [isModalOpen]);

  const handleRowExpanding = (row: FileFolder) => store.rowClick(row, false);
  const handleHighlightFolder = (row: FileFolder) => {
    setDestinationFolder(row);
    if (row) {
      setMissingData(undefined);
    }
  };

  const handleMoveFile = addProgressBar(async () => {
    if (!hasDestinationFolder(destinationFolder)) {
      setMissingData(MissingData.TARGET_DIRECTORY);
      return;
    }

    // Handle multiple files if provided
    if (hasMovableFiles(movableFiles)) {
      await handleMultipleFileMove(destinationFolder.reference, movableFiles, () => {
        setDestinationFolder(undefined);
        closeModal();
        if (onFileMoved) {
          onFileMoved();
        }
      });
      return;
    }

    // Handle single file
    if (hasSingleMovableFile(movableFile)) {
      const res: SubmitResult<{ existingFileFromPath: string }> = await fetchData({
        url: `${config.apiEndpoints.cms}/dam/${destinationFolder.reference}`,
        method: 'POST',
        body: {
          ExistingFileFromPath: movableFile.reference,
        },
      });
      if (res.success) {
        setDestinationFolder(undefined);
        closeModal();
        if (onFileMoved) {
          onFileMoved();
        }
      } else {
        toastError(res.error.errors.existingFileFromPath[0]);
      }
    }
  });

  return {
    ...base,
    destinationFolder,
    handleMoveFile,
    handleRowExpanding,
    handleHighlightFolder,
  };
};

export const useUploaderFileManager = ({
  onDropFiles,
  onDestinationSelected,
  onClearMissingData,
  fileSelectable,
}: Omit<UploaderFileManagerProps, 'onDeleteFile'>) => {
  const { store } = useBaseFileManager({});

  const handleRowExpanding = (row: FileFolder) => store.rowClick(row, false);
  const { setActiveFolder } = useFileManagerContext();

  const damSearchAndBulk: boolean = useFeatureIsOn('admin-dam-search-and-bulk-actions');

  const handleHighlightFolder = (row: FileFolder) => {
    onDestinationSelected(row);
    setActiveFolder(row);
    if (row) {
      onClearMissingData(undefined);
    }
  };

  const handleDropFiles = (files: File[]) => {
    // Show error toast if user selects or drops in more than one file
    // and admin-dam-search-and-bulk-actions is not enabled or user is currently selecting a file to use
    if ((!damSearchAndBulk || fileSelectable !== false) && isMultipleFilesError(files)) {
      toastError('modules.contentmanagement.multipleFilesError');

      return;
    }

    // Validate total size of all files selected
    if (damSearchAndBulk && !checkFilesSizePass(files)) {
      toastError('modules.contentmanagement.maxFilesUploadSize');
      return;
    }

    onDropFiles(files.map((file) => renameFile(file, removeSpaceAndSpecialCharacter(file.name))));

    if (hasFiles(files)) {
      onClearMissingData(undefined);
    }
  };

  return {
    store,
    handleDropFiles,
    handleRowExpanding,
    handleHighlightFolder,
  };
};

export const useRootFileManager = ({ mode, explorerModalName = FILEMANAGER_MODAL }: RootFileManagerProps) => {
  const [displayMode, setDisplayMode] = useState(mode);
  const [missingData, setMissingData] = useState<MissingData>();
  const [destinationFolder, setDestinationFolder] = useState<FileFolder>();
  const [uploadableFiles, setUploadableFiles] = useState<File[]>([]);

  const rootModalIsOpen = useSelector((state) => modalSelectors.isModalOpen(state, explorerModalName));

  const { closeModal } = useModal(explorerModalName);

  const handleUploadFile = addProgressBar(
    async (overwrite: boolean = false, newFileName?: string): Promise<ServerFile | undefined> => {
      if (!uploadableFiles.length) {
        setMissingData(MissingData.UPLOAD_FILE);
        return undefined;
      }

      if (!destinationFolder) {
        setMissingData(MissingData.TARGET_DIRECTORY);
        return undefined;
      }

      const formData: FormData = new FormData();
      uploadableFiles.forEach((file) => {
        formData.append(uploadableFiles.length > 1 ? 'files' : 'file', file);
      });

      return fileUpload<ServerFile>({
        url: selectUploadEndpoint(uploadableFiles, destinationFolder),
        method: 'POST',
        query: {
          overwrite,
          ...(newFileName && { fileName: newFileName }),
        },
        body: formData,
      }).then((res: SubmitResult<MultiFileUpload>) => {
        if (res.success) {
          if (uploadableFiles.length > 1) {
            handleMultiFileResponse(res);
          } else {
            toastSuccess('modules.contentmanagement.fileUploaded');
          }

          // change id used on image urls to force a reload of all images
          // if the image uploaded is replacing one with the same name
          if (overwrite) localImgId.id = Uuid();
        } else {
          const errors: string | string[] | AppError<{}> = Object.values(res.error.errors).filter(
            (e) => e.toString().length !== 0
          );

          if (Array.isArray(errors)) {
            errors.forEach((e: string) => toastError(e));
          } else if (typeof errors === 'string') {
            toastError(errors);
          }
        }

        return res?.error?.errors && Object.values(res?.error?.errors).length > 0 ? res?.error?.errors : res.data;
      });
    }
  );

  const handleDeleteFile = (file: File) => {
    setUploadableFiles((prev) => prev.filter((f) => f.name !== file.name));
  };

  const handleClearMissingData = () => {
    if (missingData === MissingData.TARGET_DIRECTORY && !!destinationFolder) {
      setMissingData(undefined);
    }
    if (missingData === MissingData.UPLOAD_FILE && !!uploadableFiles.length) {
      setMissingData(undefined);
    }
  };

  useEffect(() => {
    if (displayMode === ExplorerMode.UPLOAD) {
      setMissingData(undefined);
    }
  }, [displayMode]);

  useEffect(() => {
    setDisplayMode(mode);
    if (!rootModalIsOpen) {
      setMissingData(undefined);
      setDestinationFolder(undefined);
      setUploadableFiles([]);
    }
  }, [rootModalIsOpen]);

  return {
    displayMode,
    missingData,
    rootModalIsOpen,
    uploadableFiles,
    setDisplayMode,
    handleUploadFile,
    handleDeleteFile,
    closeRootModal: closeModal,
    setDestinationFolder,
    setUploadableFiles,
    handleClearMissingData,
  };
};
